export interface DownloadLoanDocumentsZipParams {
  loanId: string;
  loanNumber?: string | null;
  onProgress?: (progress: DownloadLoanDocumentsZipProgress) => void;
}

export type DownloadLoanDocumentsZipResult =
  | 'downloaded'
  | 'download-started'
  | 'cancelled';
type InternalDownloadLoanDocumentsZipResult =
  | DownloadLoanDocumentsZipResult
  | 'legacy-fallback';

export type DownloadLoanDocumentsZipProgress = {
  downloadedBytes: number;
  totalBytes?: number;
};

type FileSystemWritableFileStreamLike = {
  write: (data: BufferSource | Blob | string) => Promise<void>;
  close: () => Promise<void>;
  abort?: () => Promise<void>;
};

type FileSystemFileHandleLike = {
  createWritable: () => Promise<FileSystemWritableFileStreamLike>;
};

type ShowSaveFilePickerOptionsLike = {
  suggestedName?: string;
  types?: Array<{
    description?: string;
    accept: Record<string, string[]>;
  }>;
};

type ZipExportJobStatus = 'queued' | 'running' | 'completed' | 'failed';

type ZipExportJob = {
  taskId: string;
  status: ZipExportJobStatus;
  error?: string;
  fileName?: string;
  sizeBytes?: number;
  downloadUrl?: string;
};

type ZipExportJobResponse = {
  job: ZipExportJob;
};

const DOWNLOAD_TRIGGER_DEDUP_WINDOW_MS = 5000;
let lastTriggeredDownload:
  | {
      url: string;
      triggeredAt: number;
    }
  | undefined;

class HttpResponseError extends Error {
  status: number;

  constructor(response: Response) {
    super(`HTTP ${response.status}: ${response.statusText}`);
    this.status = response.status;
  }
}

declare global {
  interface Window {
    showSaveFilePicker?: (
      options?: ShowSaveFilePickerOptionsLike,
    ) => Promise<FileSystemFileHandleLike>;
  }
}

const getRequestDetails = async (loanId: string) => {
  const { getAuthToken } = await import('../utils/authTokenHttp');
  const token = await getAuthToken();

  const { getRuntimeConfig } = await import('../../utils/runtimeConfig');
  const runtimeConfig = getRuntimeConfig();
  const apiUrl = runtimeConfig.VITE_MOS_API_URL;

  const { getCurrentTenantId } = await import('../../stores/tenant');
  const tenantId = getCurrentTenantId();

  return {
    downloadUrl: `${apiUrl}/loans/${loanId}/documents/download-zip`,
    jobsUrl: `${apiUrl}/loans/${loanId}/documents/download-zip/jobs`,
    headers: {
      Accept: 'application/zip',
      'tenant-id': tenantId || '',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  };
};

const getDefaultZipFileName = (loanNumber?: string | null) =>
  `${loanNumber?.trim() || 'loan'}-documents.zip`;

const canStreamToDisk = () =>
  globalThis.window !== undefined &&
  typeof globalThis.window.showSaveFilePicker === 'function' &&
  !!globalThis.isSecureContext;

const getZipFileHandle = async (suggestedName: string) => {
  try {
    return await globalThis.window.showSaveFilePicker?.({
      suggestedName,
      types: [
        {
          description: 'ZIP archive',
          accept: { 'application/zip': ['.zip'] },
        },
      ],
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return null;
    }
    throw error;
  }
};

const assertOkResponse = (response: Response) => {
  if (!response.ok) {
    throw new HttpResponseError(response);
  }
};

const sleep = (ms: number) =>
  new Promise((resolve) => {
    globalThis.setTimeout(resolve, ms);
  });

const parseJsonResponse = async <T>(response: Response): Promise<T> => {
  assertOkResponse(response);
  return response.json() as Promise<T>;
};

const triggerUrlDownload = (url: string) => {
  const now = Date.now();
  if (
    lastTriggeredDownload?.url === url &&
    now - lastTriggeredDownload.triggeredAt < DOWNLOAD_TRIGGER_DEDUP_WINDOW_MS
  ) {
    return;
  }
  lastTriggeredDownload = { url, triggeredAt: now };

  const link = document.createElement('a');
  link.href = url;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
};

const downloadSignedUrl = async ({
  url,
  fileHandle,
  onProgress,
}: {
  url: string;
  fileHandle?: FileSystemFileHandleLike;
  onProgress?: (progress: DownloadLoanDocumentsZipProgress) => void;
}): Promise<DownloadLoanDocumentsZipResult> => {
  if (!fileHandle) {
    triggerUrlDownload(url);
    return 'download-started';
  }

  try {
    const response = await fetch(url);
    assertOkResponse(response);
    await streamResponseToFile({ response, fileHandle, onProgress });
  } catch {
    triggerUrlDownload(url);
    return 'download-started';
  }

  return 'downloaded';
};

const createZipExportJob = async ({
  jobsUrl,
  headers,
}: {
  jobsUrl: string;
  headers: HeadersInit;
}) => {
  const response = await fetch(jobsUrl, {
    method: 'POST',
    headers: {
      ...headers,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });

  return parseJsonResponse<ZipExportJobResponse>(response);
};

const getZipExportJob = async ({
  jobsUrl,
  taskId,
  headers,
}: {
  jobsUrl: string;
  taskId: string;
  headers: HeadersInit;
}) => {
  const response = await fetch(`${jobsUrl}/${taskId}`, {
    method: 'GET',
    headers: {
      ...headers,
      Accept: 'application/json',
    },
  });

  return parseJsonResponse<ZipExportJobResponse>(response);
};

const waitForZipExportJob = async ({
  jobsUrl,
  initialJob,
  headers,
}: {
  jobsUrl: string;
  initialJob: ZipExportJob;
  headers: HeadersInit;
}) => {
  const deadline = Date.now() + 30 * 60 * 1000;
  let job = initialJob;

  while (Date.now() < deadline) {
    if (job.status === 'completed') {
      return job;
    }

    if (job.status === 'failed') {
      throw new Error(job.error || 'Failed to prepare loan documents ZIP');
    }

    await sleep(2000);
    const response = await getZipExportJob({
      jobsUrl,
      taskId: job.taskId,
      headers,
    });
    job = response.job;
  }

  throw new Error('Timed out while preparing loan documents ZIP');
};

const downloadWithExportJob = async ({
  jobsUrl,
  headers,
}: {
  jobsUrl: string;
  headers: HeadersInit;
}): Promise<InternalDownloadLoanDocumentsZipResult> => {
  let initialJob: ZipExportJob;
  try {
    const response = await createZipExportJob({ jobsUrl, headers });
    initialJob = response.job;
  } catch (error) {
    if (error instanceof HttpResponseError && error.status === 404) {
      return 'legacy-fallback';
    }
    throw error;
  }

  const completedJob = await waitForZipExportJob({
    jobsUrl,
    initialJob,
    headers,
  });

  if (completedJob.downloadUrl) {
    return downloadSignedUrl({ url: completedJob.downloadUrl });
  }

  throw new Error(
    'Loan documents ZIP export completed without a signed download URL.',
  );
};

const getContentLength = (response: Response): number | undefined => {
  const contentLength = Number(response.headers.get('content-length'));
  return Number.isFinite(contentLength) && contentLength > 0
    ? contentLength
    : undefined;
};

const createProgressReporter = (
  response: Response,
  onProgress?: (progress: DownloadLoanDocumentsZipProgress) => void,
) => {
  const totalBytes = getContentLength(response);
  let downloadedBytes = 0;

  return (chunkBytes: number) => {
    downloadedBytes += chunkBytes;
    onProgress?.({ downloadedBytes, totalBytes });
  };
};

const streamResponseToFile = async ({
  response,
  fileHandle,
  onProgress,
}: {
  response: Response;
  fileHandle: FileSystemFileHandleLike;
  onProgress?: (progress: DownloadLoanDocumentsZipProgress) => void;
}) => {
  if (!response.body) {
    throw new Error('Streaming download is not supported by this browser');
  }

  const reader = response.body.getReader();
  const writable = await fileHandle.createWritable();
  const reportProgress = createProgressReporter(response, onProgress);

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      if (value) {
        reportProgress(value.byteLength);
        await writable.write(value);
      }
    }
    await writable.close();
  } catch (error) {
    await writable.abort?.();
    throw error;
  } finally {
    reader.releaseLock();
  }
};

export const downloadLoanDocumentsZip = async ({
  loanId,
  loanNumber,
  onProgress,
}: DownloadLoanDocumentsZipParams): Promise<DownloadLoanDocumentsZipResult> => {
  const requestDetails = await getRequestDetails(loanId);

  const result = await downloadWithExportJob({
    jobsUrl: requestDetails.jobsUrl,
    headers: requestDetails.headers,
  });

  if (result !== 'legacy-fallback') {
    return result;
  }

  const canUseFilePicker = canStreamToDisk();
  const fileHandle: FileSystemFileHandleLike | undefined = canUseFilePicker
    ? ((await getZipFileHandle(getDefaultZipFileName(loanNumber))) ?? undefined)
    : undefined;

  if (canUseFilePicker && !fileHandle) {
    return 'cancelled';
  }

  if (fileHandle) {
    const response = await fetch(requestDetails.downloadUrl, {
      method: 'GET',
      headers: requestDetails.headers,
    });

    assertOkResponse(response);
    await streamResponseToFile({ response, fileHandle, onProgress });
    return 'downloaded';
  }

  throw new Error(
    'Loan documents ZIP download requires browser file streaming support.',
  );
};

export default downloadLoanDocumentsZip;
